import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Phone, Building2, LogOut } from 'lucide-react';
import { CircleCheckBig, MessageCircle } from 'lucide-react';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import logo from '../../assets/logo_sigo.png';
import { usePatientAppHome } from './hooks/usePatientAppHome';

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const accessToken = searchParams.get('token');

  const {
    fetchAppointments: fetchAppointmentsRequest,
    loginWithToken,
    confirmAppointment,
  } = usePatientAppHome();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [appointments, setAppointments] = useState([]);

  const fetchAppointments = useCallback(() => {
    setLoading(true);
    fetchAppointmentsRequest()
      .then((returnFromApi) => {
        setAppointments(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/patient-app/login');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, fetchAppointmentsRequest]);

  useEffect(() => {
    if (accessToken) {
      setLoading(true);
      loginWithToken(accessToken)
        .then((returnFromApi) => {
          sessionStorage.setItem('token', returnFromApi.data.token);
          setSearchParams({}, { replace: true });
        })
        .catch((returnFromApi) => {
          console.error('ops! ocorreu um erro' + returnFromApi);
          navigate('/patient-app/login');
        });
      return;
    }
    fetchAppointments();
  }, [accessToken, fetchAppointments, navigate, setSearchParams, loginWithToken]);

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    navigate('/patient-app/login');
  };

  const handleContactClinic = (clinicPhone) => {
    const phoneNumber = clinicPhone.replace(/\D/g, '');
    window.open(`https://wa.me/55${phoneNumber}`, '_blank');
  };

  const handleConfirmAppointment = (appointmentId) => {
    setLoading(true);
    confirmAppointment(appointmentId)
      .then(() => {
        setAppointments((previous) =>
          previous.map((appointment) =>
            appointment.id === appointmentId
              ? {
                  ...appointment,
                  status: 'CONFIRMED_BY_PATIENT',
                  statusDescription: 'Confirmada pelo Paciente',
                }
              : appointment,
          ),
        );
        setSuccessMessage('Consulta confirmada com sucesso!');
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/patient-app/login');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ maxWidth: 480, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img src={logo} alt="Logo" style={{ maxWidth: 140, objectFit: 'contain' }} />
          <IconButton aria-label="sair" onClick={handleLogout}>
            <LogOut size={22} />
          </IconButton>
        </Box>

        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#000' }}>
          Minhas Consultas
        </Typography>

        {appointments.length > 0 ? (
          <Stack spacing={2}>
            {appointments.map((appointment) => (
              <Card key={appointment.id} sx={{ borderRadius: 4 }} elevation={2}>
                <CardHeader
                  title={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {appointment.professionalName}
                    </Typography>
                  }
                  action={
                    <Chip
                      label={appointment.statusDescription}
                      size="small"
                      sx={{ mr: 1, mt: 1 }}
                    />
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Calendar size={18} />
                      <Typography variant="body2">{appointment.date}</Typography>
                      <Clock size={18} />
                      <Typography variant="body2">{appointment.hour}</Typography>
                    </Stack>

                    <Divider />

                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Building2 size={18} style={{ marginTop: 2 }} />
                      <Typography variant="body2">{appointment.clinicName}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <MapPin size={18} style={{ marginTop: 2 }} />
                      <Typography variant="body2">{appointment.clinicAddress}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Phone size={18} />
                      <Typography variant="body2">{appointment.clinicPhone}</Typography>
                    </Stack>
                  </Stack>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      startIcon={<CircleCheckBig size={18} />}
                      disabled={appointment.status !== 'SCHEDULED'}
                      onClick={() => handleConfirmAppointment(appointment.id)}
                    >
                      Confirmar Consulta
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="success"
                      startIcon={<MessageCircle size={18} />}
                      onClick={() => handleContactClinic(appointment.clinicPhone)}
                    >
                      Contactar Clínica
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            ))}
          </Stack>
        ) : (
          !isLoading && (
            <Card sx={{ borderRadius: 4, p: 3, textAlign: 'center' }} elevation={2}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma consulta agendada.
              </Typography>
            </Card>
          )
        )}
      </Box>
    </Box>
  );
}
